import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const TEMPLATE_HEADERS = [
  'Typenschein / Fahrzeug-Key',
  'Antriebsart',
  'Alter',
  'Gefahrene Kilometer im aktuellen Jahr',
];

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function findColumnKey(row, acceptedHeaders) {
  const keys = Object.keys(row);

  return keys.find(key =>
    acceptedHeaders.includes(normalizeHeader(key))
  );
}

function normalizeDriveType(value) {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (
    normalized === 'verbrenner'
  ) {
    return 'combustion';
  }

  if (
    normalized === 'elektro' ||
    normalized === 'elektrisch'
  ) {
    return 'electric';
  }

  if (normalized === 'hybrid') {
    return 'hybrid';
  }

  return '';
}

function parseNumber(value) {
  const normalized = String(value ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(/'/g, '')
    .replace(',', '.');

  if (!normalized) {
    return null;
  }

  const number = Number(normalized);

  return Number.isFinite(number)
    ? number
    : null;
}

function roundAverage(value) {
  return Math.round(value * 10) / 10;
}

export function calculateFleetAverages(vehicles) {
  const categories = {
    combustion: [],
    electric: [],
    hybrid: [],
  };

  vehicles.forEach(vehicle => {
    if (categories[vehicle.driveType]) {
      categories[vehicle.driveType].push(vehicle);
    }
  });

  return Object.fromEntries(
    Object.entries(categories).map(([category, categoryVehicles]) => {
      if (categoryVehicles.length === 0) {
        return [
          category,
          {
            count: 0,
            averageAge: 0,
            averageKm: 0,
          },
        ];
      }

      const ageSum = categoryVehicles.reduce(
        (sum, vehicle) => sum + vehicle.age,
        0
      );

      const kmSum = categoryVehicles.reduce(
        (sum, vehicle) => sum + vehicle.mileage,
        0
      );

      return [
        category,
        {
          count: categoryVehicles.length,
          averageAge: roundAverage(
            ageSum / categoryVehicles.length
          ),
          averageKm: roundAverage(
            kmSum / categoryVehicles.length
          ),
        },
      ];
    })
  );
}

export async function downloadFleetExcelTemplate() {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Ersatzwagenflotte');

  worksheet.columns = [
    { header: TEMPLATE_HEADERS[0], key: 'typeApprovalNumber', width: 30 },
    { header: TEMPLATE_HEADERS[1], key: 'driveType', width: 20 },
    { header: TEMPLATE_HEADERS[2], key: 'age', width: 12 },
    { header: TEMPLATE_HEADERS[3], key: 'mileage', width: 38 },
  ];

  worksheet.getRow(1).font = { bold: true };
  worksheet.dataValidations.add('B2:B1001', {
    type: 'list',
    allowBlank: true,
    formulae: ['"Verbrenner,Elektro,Hybrid"'],
    showErrorMessage: true,
    errorStyle: 'stop',
    errorTitle: 'Ungültige Antriebsart',
    error: 'Bitte wählen Sie Verbrenner, Elektro oder Hybrid.',
  });

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], { type: EXCEL_MIME_TYPE }),
    'GreenOne_Ersatzwagenflotte_Template.xlsx'
  );
}

export async function parseFleetExcelFile(file) {
  if (!file) {
    throw new Error('Bitte wählen Sie eine Excel-Datei aus.');
  }

  const extension = file.name
    .split('.')
    .pop()
    ?.toLowerCase();

  if (!['xlsx', 'xls'].includes(extension)) {
    throw new Error(
      'Ungültiges Dateiformat. Erlaubt sind nur XLSX- und XLS-Dateien.'
    );
  }

  let workbook;

  try {
    const arrayBuffer = await file.arrayBuffer();

    workbook = XLSX.read(arrayBuffer, {
      type: 'array',
    });
  } catch {
    throw new Error(
      'Die Excel-Datei konnte nicht gelesen werden.'
    );
  }

  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    throw new Error(
      'Die Excel-Datei enthält kein Tabellenblatt.'
    );
  }

  const worksheet = workbook.Sheets[firstSheetName];

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    defval: '',
    raw: false,
  });

  if (rows.length === 0) {
    throw new Error(
      'Die Excel-Datei enthält keine Fahrzeugdaten.'
    );
  }

  const firstRow = rows[0];

  const typeApprovalKey = findColumnKey(firstRow, [
    'typenschein / fahrzeug-key',
    'typenschein',
    'fahrzeug-key',
    'fahrzeug key',
  ]);

  const driveTypeKey = findColumnKey(firstRow, [
    'antriebsart',
    'antrieb',
  ]);

  const ageKey = findColumnKey(firstRow, [
    'alter',
    'fahrzeugalter',
    'fahrzeug-alter',
  ]);

  const mileageKey = findColumnKey(firstRow, [
    'gefahrene kilometer im aktuellen jahr',
    'kilometer im aktuellen jahr',
    'km im aktuellen jahr',
    'jahreskilometer',
  ]);

  if (
    !typeApprovalKey ||
    !driveTypeKey ||
    !ageKey ||
    !mileageKey
  ) {
    throw new Error(
      'Die Pflichtspalten „Typenschein / Fahrzeug-Key“, „Antriebsart“, „Alter“ und „Gefahrene Kilometer im aktuellen Jahr“ fehlen.'
    );
  }

  const vehicles = [];

  rows.forEach((row, index) => {
    const excelRowNumber = index + 2;

    const typeApprovalNumber = String(
      row[typeApprovalKey] ?? ''
    ).trim();

    const driveTypeRaw = String(
      row[driveTypeKey] ?? ''
    ).trim();

    const ageRaw = String(
      row[ageKey] ?? ''
    ).trim();

    const mileageRaw = String(
      row[mileageKey] ?? ''
    ).trim();

    const rowIsEmpty =
      typeApprovalNumber === '' &&
      driveTypeRaw === '' &&
      ageRaw === '' &&
      mileageRaw === '';

    if (rowIsEmpty) {
      return;
    }

    if (!typeApprovalNumber) {
      throw new Error(
        `Zeile ${excelRowNumber}: Typenschein oder Fahrzeug-Key fehlt.`
      );
    }

    const driveType =
      normalizeDriveType(driveTypeRaw);

    if (!driveType) {
      throw new Error(
        `Zeile ${excelRowNumber}: Antriebsart muss „Verbrenner“, „Elektro“ oder „Hybrid“ sein.`
      );
    }

    const age = parseNumber(ageRaw);

    if (age == null) {
      throw new Error(
        `Zeile ${excelRowNumber}: Alter muss eine gültige Zahl sein.`
      );
    }

    if (age < 0) {
      throw new Error(
        `Zeile ${excelRowNumber}: Alter darf nicht negativ sein.`
      );
    }

    const mileage = parseNumber(mileageRaw);

    if (mileage == null) {
      throw new Error(
        `Zeile ${excelRowNumber}: Gefahrene Kilometer im aktuellen Jahr müssen eine gültige Zahl sein.`
      );
    }

    if (mileage < 0) {
      throw new Error(
        `Zeile ${excelRowNumber}: Gefahrene Kilometer im aktuellen Jahr dürfen nicht negativ sein.`
      );
    }

    vehicles.push({
      typeApprovalNumber,
      driveType,
      age,
      mileage,
    });
  });

  if (vehicles.length === 0) {
    throw new Error(
      'Die Excel-Datei enthält keine vollständigen Fahrzeugdaten.'
    );
  }

  return vehicles;
}
